import { useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Linking, RefreshControl, Platform } from 'react-native'
import Svg, { Path, Rect, Circle, Polygon } from 'react-native-svg'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAuth } from '../../lib/AuthContext'
import { storage } from '../../lib/storage'
import { CACHE_KEY, processPodio, SkeletonHome, SectionLabel, Carousel, ProssimaCard } from '../../components/Home/ui'
import LiveBanner from '../../components/Home/LiveBanner'
import PickSection from '../../components/Home/PickSection'
import PodioCard from '../../components/Home/PodioCard'
import OnboardingOverlay from '../../components/Home/OnboardingOverlay'
import JerseyCelebration from '../../components/Home/JerseyCelebration'
import { WelcomeHeader, AvatarInviteCard } from '../../components/Home/WelcomeHero'
import AnnouncementBanner from '../../components/Home/AnnouncementBanner'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import Ionicons from '@expo/vector-icons/Ionicons'
import GradientButton from '../../components/GradientButton'
import QuizModal from '../../components/QuizModal'
import ConnectionsModal from '../../components/ConnectionsModal'

const SOCIAL = [
  { url: 'https://www.instagram.com/virtuacycling/', label: 'Instagram' },
  { url: 'https://www.facebook.com/virtuacycling', label: 'Facebook' },
  { url: 'https://www.youtube.com/@virtuacycling1448', label: 'YouTube' },
  { url: 'https://x.com/VirtuaCycling', label: 'X' },
  { url: 'https://t.me/virtuacycling', label: 'Telegram' },
]

const TTL_MS = 5 * 60 * 1000

export default function Home() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { nickname, leagueId, user } = useAuth()
  const router = useRouter()

  const [picks, setPicks]               = useState([])
  const [current, setCurrent]           = useState([])
  const [podioStages, setPodioStages]   = useState([])
  const [upcoming, setUpcoming]         = useState([])
  const [announcement, setAnnouncement] = useState(null)
  const [error, setError]               = useState(null)
  const [loading, setLoading]           = useState(true)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [refreshing, setRefreshing]     = useState(false)
  const [showQuiz, setShowQuiz]         = useState(false)
  const [showConnections, setShowConnections] = useState(false)
  const [quizEnabled, setQuizEnabled]             = useState(true)
  const [connectionsEnabled, setConnectionsEnabled] = useState(true)

  const applyData = useCallback((data) => {
    setPicks(data.picks ?? [])
    setCurrent(data.current ?? [])
    setUpcoming(data.upcoming ?? [])
    setPodioStages(processPodio(data.podio ?? []))
    setAnnouncement(data.announcement ?? null)
    setQuizEnabled(data.quiz_enabled ?? true)
    setConnectionsEnabled(data.connections_enabled ?? true)
  }, [])

  useEffect(() => {
    if (!user?.id) return
    supabase.from('dim_user').select('onboarding_done').eq('id_user', user.id).single()
      .then(({ data }) => { if (data && !data.onboarding_done) setShowOnboarding(true) })
  }, [user?.id])

  const load = useCallback(async (forceRefresh = false) => {
    try {
      const cached = await storage.get(CACHE_KEY(leagueId))
      if (cached) {
        const parsed = JSON.parse(cached)
        const cachedData = parsed.data ?? parsed
        const timestamp = parsed.timestamp ?? 0
        const isStale = Date.now() - timestamp > TTL_MS
        applyData(cachedData)
        setLoading(false)
        if (!isStale && !forceRefresh) return
      }
    } catch {/* ignore */}

    try {
      const { data, error: err } = await supabase.rpc('fn_home_data', {
        p_id_league: leagueId,
        p_id_user: user?.id ?? null,
      })
      if (err) throw err
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      applyData(parsed)
      setError(null)
      try {
        await storage.set(CACHE_KEY(leagueId), JSON.stringify({ data: parsed, timestamp: Date.now() }))
      } catch {/* ignore */}
    } catch (err) {
      console.error('[Home] errore:', err)
      setError(t('common.error_data'))
    } finally {
      setLoading(false)
    }
  }, [leagueId, t, applyData, user?.id])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    await load(true)
    setRefreshing(false)
  }, [load])

  useEffect(() => { load() }, [load])

  const goToPick = (item) => {
    router.push(`/pick?id_race=${item.id_race}&id_stage=${item.id_stage}`)
  }

  return (
    <>
      {showOnboarding && <OnboardingOverlay userId={user?.id} onDone={() => setShowOnboarding(false)} />}
      <JerseyCelebration enabled={!showOnboarding && !loading} />
      <ScrollView
        style={[styles.container, { backgroundColor: tc.bg }]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={tc.rosa} colors={[tc.rosa]} />}
        contentContainerStyle={styles.content}
      >
        {loading && !picks.length ? (
          <SkeletonHome />
        ) : (
          <>
            <WelcomeHeader nickname={nickname} picks={picks} />
            {error && (
              <View style={styles.errorBanner} accessibilityRole="alert">
                <Text style={styles.errorText}>{error}</Text>
                <TouchableOpacity onPress={onRefresh} style={styles.retryBtn} accessibilityRole="button">
                  <Text style={styles.retryText}>{t('home.retry')}</Text>
                </TouchableOpacity>
              </View>
            )}
            <AnnouncementBanner announcement={announcement} />
            <LiveBanner items={current} onGo={goToPick} />
            <AvatarInviteCard />
            <PickSection picks={picks} onGo={goToPick} loading={false} />

            {/* ── Quiz Card — governata da admin (dim_app_config.quiz_enabled) ── */}
            {quizEnabled && (
              <TouchableOpacity
                style={[styles.quizCard, { backgroundColor: tc.bgCard, borderColor: tc.rosaBorder }]}
                onPress={() => setShowQuiz(true)}
                activeOpacity={0.8}
              >
                <View style={[styles.quizIcon, { backgroundColor: tc.rosaSubtle }]}>
                  <MaterialCommunityIcons name="bike" size={22} color={tc.rosaText} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.quizTitle, { color: tc.text }]}>Quiz — Vuelta a España</Text>
                  <Text style={[styles.quizSub, { color: tc.textMuted }]}>{t('quiz.card_sub')}</Text>
                </View>
                <Ionicons name="chevron-forward" size={16} color={tc.textMuted} />
              </TouchableOpacity>
            )}

            {/* ── Connections Card — governata da admin (dim_app_config.connections_enabled) ── */}
            {connectionsEnabled && (
              <TouchableOpacity
                style={[styles.quizCard, { backgroundColor: tc.bgCard, borderColor: tc.rosaBorder }]}
                onPress={() => setShowConnections(true)}
                activeOpacity={0.8}
              >
                <View style={[styles.quizIcon, { backgroundColor: tc.rosaSubtle }]}>
                  <MaterialCommunityIcons name="link-variant" size={22} color={tc.rosaText} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.quizTitle, { color: tc.text }]}>{t('connections.card_title', 'Puzzle Game')}</Text>
                  <Text style={[styles.quizSub, { color: tc.textMuted }]}>{t('connections.card_sub', 'Trova i 4 gruppi nascosti')}</Text>
                </View>
                <Ionicons name="chevron-forward" size={16} color={tc.textMuted} />
              </TouchableOpacity>
            )}

            {podioStages.length > 0 && (
              <View style={{ marginBottom: 24 }}>
                <SectionLabel mb={10}>{t('home.section_results')}</SectionLabel>
                <Carousel>
                  {podioStages.slice(0, 5).map((s) => (
                    <PodioCard key={s.id_race} {...s} />
                  ))}
                </Carousel>
              </View>
            )}

            {upcoming.length > 0 && (
              <View style={{ marginBottom: 24 }}>
                <SectionLabel mb={10}>{t('home.section_upcoming')}</SectionLabel>
                <Carousel>
                  {upcoming.map((u, i) => (
                    <ProssimaCard key={i} item={u} />
                  ))}
                </Carousel>
              </View>
            )}
          </>
        )}

        {/* Ko-fi */}
        <View style={{ paddingHorizontal: 16, paddingBottom: 24 }}>
          <GradientButton
            onPress={() => Linking.openURL('https://ko-fi.com/virtuacycling')}
            size="md"
            icon={<MaterialCommunityIcons name="glass-mug-variant" size={16} color={tc.brandText} />}
            iconPosition="left"
          >
            {t('home.kofi_cta')}
          </GradientButton>
        </View>

        {/* Social icons */}
        <View style={styles.socialRow}>
          {SOCIAL.map(({ url, label }) => (
            <TouchableOpacity
              key={label}
              style={[styles.socialBtn, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}
              onPress={() => Linking.openURL(url)}
            >
              {label === 'Instagram' && (
                <Svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={tc.textMuted} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <Rect x={2} y={2} width={20} height={20} rx={5} />
                  <Circle cx={12} cy={12} r={4} />
                  <Circle cx={17.5} cy={6.5} r={1} fill={tc.textMuted} stroke="none" />
                </Svg>
              )}
              {label === 'Facebook' && (
                <Svg width={22} height={22} viewBox="0 0 24 24" fill={tc.textMuted}>
                  <Path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </Svg>
              )}
              {label === 'YouTube' && (
                <Svg width={22} height={22} viewBox="0 0 24 24">
                  <Path fill={tc.textMuted} d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
                  <Polygon fill="white" points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
                </Svg>
              )}
              {label === 'X' && (
                <Svg width={20} height={20} viewBox="0 0 24 24" fill={tc.textMuted}>
                  <Path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </Svg>
              )}
              {label === 'Telegram' && (
                <Svg width={22} height={22} viewBox="0 0 24 24" fill={tc.textMuted}>
                  <Path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8l-1.68 7.92c-.12.56-.46.7-.93.44l-2.58-1.9-1.24 1.2c-.14.14-.26.26-.52.26l.18-2.62 4.74-4.28c.2-.18-.04-.28-.32-.1L7.46 14.5l-2.52-.79c-.55-.17-.56-.55.12-.82l9.86-3.8c.46-.17.86.11.72.71z" />
                </Svg>
              )}
            </TouchableOpacity>
          ))}
        </View>

        <Text style={[styles.socialLabel, { color: tc.textMuted }]}>
          {t('home.social')}
        </Text>
      </ScrollView>

      {/* Quiz Modal */}
      <QuizModal
        visible={showQuiz}
        onClose={() => setShowQuiz(false)}
      />

      {/* Connections Modal */}
      <ConnectionsModal
        visible={showConnections}
        onClose={() => setShowConnections(false)}
      />
    </>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container:  { flex: 1 },
  content:    { padding: 16, paddingBottom: 32 },
  errorBanner: { backgroundColor: colors.errorBg, borderWidth: 1, borderColor: tc.rosaBorder, borderRadius: 10, padding: 12, marginBottom: 20 },
  errorText:  { color: tc.rosaText, fontSize: 14 },
  retryBtn:   { alignSelf: 'flex-start', minHeight: 44, justifyContent: 'center', marginTop: 4 },
  retryText:  { color: tc.rosaText, fontSize: 14, fontWeight: '700', textDecorationLine: 'underline' },
  socialRow:  { flexDirection: 'row', justifyContent: 'center', gap: 16, paddingTop: 24, paddingHorizontal: 16, paddingBottom: 8 },
  socialBtn:  { width: 44, height: 44, borderRadius: 22, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  socialLabel: { textAlign: 'center', fontSize: 11, paddingBottom: 24, opacity: 0.5 },
  quizCard:   { flexDirection: 'row', alignItems: 'center', gap: 12, borderRadius: 14, borderWidth: 1, borderLeftWidth: 3, padding: 14, marginBottom: 16 },
  quizIcon:   { width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center' },
  quizTitle:  { fontFamily: fonts.condensedBold, fontSize: 15 },
  quizSub:    { fontSize: 12, marginTop: 2 },
})